// Bottom sheet for mobile: "Which DAW do you use?" — routes to the right
// next step (Ableton: send to Live, or send yourself the link; Audiotool:
// open; other: waitlist).
// Three states: pick, ableton, other.

import { LiveDifferencesNote } from "./LiveDifferencesNote";
import { useEffect, useState, type FormEvent } from "react";
import { getSession } from "@web-shared/lib/account";
import { useNavigate } from "react-router";
import { cn } from "@web-shared/lib/utils";
import { filesUploadPath, localRoomPath } from "@/lib/ctas";
import { webApi, json } from "@web-shared/lib/web-api";
import { track } from "@web-shared/lib/analytics";
import { useAuthGate } from "../auth";
import { useSession } from "../session";
import { shareAbletonLink } from "./share";

type Step = "pick" | "ableton" | "audiotool" | "other";

export function DawPicker({
  seedId,
  seedTitle,
  onClose,
}: {
  seedId: string;
  seedTitle: string;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step>("pick");
  const navigate = useNavigate();

  // Lock body scroll while the sheet is open.
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const pick = (next: Step) => {
    if (next === "ableton" || next === "audiotool") track("remix_daw_picked", { seedId, detail: next });
    setStep(next);
  };

  const goToEditor = () => {
    track("remix_daw_picked", { seedId, detail: "browser" });
    onClose();
    navigate(localRoomPath(seedId));
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />

      {/* Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl bg-raised border-t border-white/10 px-5 pt-3 pb-[calc(4rem+env(safe-area-inset-bottom))] shadow-[0_-20px_40px_rgba(0,0,0,0.6)] max-h-[85dvh] overflow-y-auto overscroll-contain">
        <div className="w-10 h-1 rounded-full bg-white/20 mx-auto" />

        {step === "pick" && <PickStep onPick={pick} onPlayHere={goToEditor} />}
        {step === "ableton" && (
          <AbletonStep seedId={seedId} seedTitle={seedTitle} onBack={() => setStep("pick")} />
        )}
        {step === "audiotool" && (
          <AudiotoolStep seedId={seedId} seedTitle={seedTitle} onBack={() => setStep("pick")} />
        )}
        {step === "other" && (
          <OtherStep seedId={seedId} onBack={() => setStep("pick")} />
        )}
      </div>
    </>
  );
}

function PickStep({ onPick, onPlayHere }: { onPick: (step: Step) => void; onPlayHere: () => void }) {
  return (
    <>
      <h3 className="mt-4 text-2xl font-black tracking-tight">Which DAW do you use?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Seedbed opens seeds straight into these two. More are on the way.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        <DawOption
          name="Ableton Live 12"
          sub="Stems land on tracks in your Live Set"
          badge="Supported"
          supported
          icon={<img src="/ableton-icon.webp" alt="" className="h-4 w-auto rounded invert" />}
          onClick={() => onPick("ableton")}
        />
        <DawOption
          name="Audiotool"
          sub="Free, runs in any desktop browser"
          badge="Supported"
          supported
          icon={<img src="/audiotool-icon.webp" alt="" className="size-6 rounded" />}
          onClick={() => onPick("audiotool")}
        />
        <DawOption
          name="Something else"
          sub="FL Studio, Logic, Bitwig, Pro Tools..."
          badge="Not yet"
          supported={false}
          onClick={() => onPick("other")}
        />
      </div>
      <button
        type="button"
        onClick={onPlayHere}
        className="mt-4 h-11 w-full rounded-2xl border border-white/15 flex items-center justify-center text-sm font-semibold"
      >
        Or play with the stems here
      </button>
    </>
  );
}

function DawOption({
  name,
  sub,
  badge,
  supported,
  icon,
  onClick,
}: {
  name: string;
  sub: string;
  badge: string;
  supported: boolean;
  icon?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full p-3.5 rounded-2xl border flex items-center gap-3 text-left",
        supported
          ? "border-white/10 bg-white/[0.03]"
          : "border-white/10 bg-white/[0.03] opacity-90",
      )}
    >
      <span className={cn(
        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden",
        supported ? "bg-[#0d0d12] border border-white/10" : "bg-white/5 text-muted-foreground/60",
      )}>
        {icon ?? (
          <svg className="size-5" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" viewBox="0 0 24 24"><circle cx={12} cy={12} r={8} /><path d="M12 8v4M12 16h.01" /></svg>
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold">{name}</span>
        <span className={cn("block text-xs", supported ? "text-muted-foreground" : "text-muted-foreground/60")}>{sub}</span>
      </span>
      <span className={cn(
        "text-[10px] font-bold tracking-wide px-2 py-1 rounded-full",
        badge === "Supported" ? "bg-primary/15 text-primary" : "bg-white/5 text-muted-foreground/60",
      )}>
        {badge}
      </span>
    </button>
  );
}

function AbletonStep({
  seedId,
  seedTitle,
  onBack,
}: {
  seedId: string;
  seedTitle: string;
  onBack: () => void;
}) {
  const session = useSession();
  const gate = useAuthGate();
  const [sub, setSub] = useState<"ask" | "sent" | "no-ext" | "no-ableton" | "link">("ask");
  const [sending, setSending] = useState(false);
  const [linkResult, setLinkResult] = useState<"shared" | "copied" | "failed" | null>(null);

  // Reads the session itself rather than the render's: after the sign-in
  // dialog this runs from the gate, before the sheet has re-rendered.
  const sendToLive = async () => {
    if (!getSession() || sending) return;
    setSending(true);
    try {
      await webApi(`/seeds/${encodeURIComponent(seedId)}/send-to-live`, json("POST", {}));
      track("sent_to_ableton", { seedId });
      track("remix_started", { seedId, detail: "ableton" });
    } catch { /* best effort */ }
    setSending(false);
    setSub("sent");
  };

  const sendLink = async () => {
    setLinkResult(await shareAbletonLink({ id: seedId, title: seedTitle }));
  };

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0">
          <img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold truncate">{seedTitle}</h3>
          <p className="text-[11px] text-muted-foreground">Ableton Live 12</p>
        </div>
      </div>

      {sub === "ask" && (
        <>
          <LiveDifferencesNote seedId={seedId} />
          <p className="mt-4 text-sm font-semibold">Do you have the Seedbed extension?</p>
          <div className="mt-3 flex flex-col gap-2">
            {session ? (
              <button
                type="button"
                onClick={() => void sendToLive()}
                disabled={sending}
                className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center disabled:opacity-50"
              >
                {sending ? "Sending..." : "Yes, send to Live"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => gate.requireSignIn("send_to_live", "Sign in to send this to Live", () => void sendToLive())}
                className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
              >
                Sign in to send to Live
              </button>
            )}
            <button type="button" onClick={() => setSub("no-ext")} className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center">
              No, I need the extension
            </button>
            <button type="button" onClick={() => setSub("link")} className="text-xs text-muted-foreground mt-1 text-center">
              Or send yourself the link
            </button>
          </div>
        </>
      )}

      {sub === "sent" && (
        <div className="mt-4 flex flex-col items-center text-center gap-3">
          <div className="size-12 rounded-full bg-primary/15 flex items-center justify-center">
            <svg className="size-6 text-primary" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <div>
            <h3 className="text-base font-bold">Sent to Live</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Open Seedbed in Live. <span className="text-foreground font-medium">{seedTitle}</span> is waiting in your notifications.
            </p>
          </div>
        </div>
      )}

      {sub === "no-ext" && (
        <>
          <p className="mt-4 text-sm font-semibold">Do you have Ableton Live 12?</p>
          <p className="mt-1 text-xs text-muted-foreground">The extension requires Live 12 Suite.</p>
          <div className="mt-3 flex flex-col gap-2">
            <button type="button" onClick={() => setSub("link")} className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center">
              Yes, send me the install link
            </button>
            <button type="button" onClick={() => setSub("no-ableton")} className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center">
              No
            </button>
          </div>
        </>
      )}

      {sub === "no-ableton" && (
        <div className="mt-4 text-center">
          <h3 className="text-base font-bold">Ableton Live 12 required</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            The Seedbed extension only works with Live 12 Suite. You can still remix in Audiotool or the browser editor.
          </p>
        </div>
      )}

      {sub === "link" && (
        <>
          <p className="mt-4 text-sm text-muted-foreground">
            Open it on your computer to get {seedTitle} into Live.
          </p>
          {linkResult === "shared" || linkResult === "copied" ? (
            <div className="mt-3 rounded-xl bg-primary/10 border border-primary/30 p-3 text-center">
              <div className="text-sm font-semibold text-primary">{linkResult === "shared" ? "Link sent" : "Link copied"}</div>
              <div className="text-xs text-muted-foreground mt-0.5">Open it on your computer.</div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void sendLink()}
              className="mt-3 h-11 w-full rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
            >
              {linkResult === "failed" ? "Try again" : "Send the link"}
            </button>
          )}
        </>
      )}

      <button type="button" onClick={sub === "ask" ? onBack : () => setSub("ask")} className="mt-3 text-sm text-muted-foreground">
        ← Back
      </button>
    </>
  );
}

function AudiotoolStep({ seedId, seedTitle, onBack }: { seedId: string; seedTitle: string; onBack: () => void }) {
  const navigate = useNavigate();
  const [sub, setSub] = useState<"main" | "what">("main");

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
          <img src="/audiotool-icon.webp" alt="" className="size-7 rounded" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold">
            {seedTitle ? <>Opening <span className="break-words">{seedTitle}</span> in Audiotool</> : "Opening in Audiotool"}
          </h3>
          <p className="text-[11px] text-muted-foreground">Free, runs in any browser</p>
        </div>
      </div>

      {sub === "main" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground">
            A new Audiotool project, every stem on its own track, ready to remix in the browser.
          </p>
          <div className="mt-4 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => { navigate(`/s/${encodeURIComponent(seedId)}/audiotool`); }}
              className="h-11 rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center gap-2"
            >
              Open Audiotool Hub
              <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8" /></svg>
            </button>
            <button type="button" onClick={() => setSub("what")} className="text-xs text-muted-foreground/60 mt-1 text-center">
              What is Audiotool?
            </button>
          </div>
        </>
      )}

      {sub === "what" && (
        <>
          <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
            Audiotool is a free browser-based DAW. Seedbed can open any seed as an Audiotool project with all its stems on separate tracks. No download, no install.
          </p>
          <button
            type="button"
            onClick={() => { navigate(`/s/${encodeURIComponent(seedId)}/audiotool`); }}
            className="mt-4 h-11 w-full rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center"
          >
            Try it with {seedTitle}
          </button>
        </>
      )}

      <button type="button" onClick={sub === "main" ? onBack : () => setSub("main")} className="mt-3 text-sm text-muted-foreground">
        ← Back
      </button>
    </>
  );
}

const DAWS = ["FL Studio", "Logic Pro", "Bitwig", "Pro Tools", "Reaper", "GarageBand", "Other"] as const;

/** Any DAW but the two: a waitlist. `embedded` is the home modal's version —
 *  the modal carries the title and the way back, and "Right here" / Audiotool
 *  were the rows beside it, so the "in the meantime" block would repeat them. */
export function OtherStep({ seedId, onBack, embedded = false }: { seedId: string; onBack?: () => void; embedded?: boolean }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("FL Studio");
  const [customDaw, setCustomDaw] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const dawValue = selected === "Other" ? customDaw.trim() : selected;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !dawValue) return;
    setSending(true);
    try {
      await webApi("/waitlist", json("POST", { email, daw: dawValue, seedId }));
    } catch {
      // best effort
    }
    setSending(false);
    setSent(true);
  };

  return (
    <>
      {embedded ? (
        <p className="-mt-3 text-sm text-muted-foreground">Tell us yours. We'll ping you when it's in.</p>
      ) : (
        <>
          <h3 className="mt-4 text-2xl font-black tracking-tight">Not in your DAW yet</h3>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Right now Seedbed opens seeds in Ableton Live and Audiotool. Tell us what you use and we'll let you know when it's supported.
          </p>
        </>
      )}
      <button
        type="button"
        onClick={() => navigate(filesUploadPath({ parent: seedId }))}
        className="mt-4 h-11 w-full rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center"
      >
        Already made your version? Upload it as a remix
      </button>
      {sent ? (
        <div className="mt-4 rounded-2xl bg-primary/10 border border-primary/30 p-4 text-center">
          <div className="text-sm font-semibold text-primary">We'll let you know when {dawValue} is supported.</div>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="mt-4 flex flex-col gap-3">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-12 w-full px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm text-foreground appearance-none"
          >
            {DAWS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {selected === "Other" && (
            <input
              type="text"
              placeholder="Which DAW?"
              value={customDaw}
              onChange={(e) => setCustomDaw(e.target.value)}
              className="h-12 w-full px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm placeholder-muted-foreground/50"
              required
              maxLength={100}
            />
          )}
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-0 h-12 px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm placeholder-muted-foreground/50"
              required
            />
            <button
              type="submit"
              disabled={sending || !dawValue}
              className="h-12 px-4 rounded-2xl bg-primary text-primary-foreground font-bold text-sm whitespace-nowrap transition-[filter] hover:brightness-110 disabled:opacity-50"
            >
              {sending ? "..." : "Notify me"}
            </button>
          </div>
        </form>
      )}
      {!embedded && (
        <>
          <div className="mt-5 pt-4 border-t border-white/10">
            <div className="text-sm font-semibold">In the meantime</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <a
                href={`/room/local/${encodeURIComponent(seedId)}`}
                className="h-11 rounded-2xl bg-primary text-primary-foreground text-sm font-bold flex items-center justify-center"
              >
                Play with the stems
              </a>
              <a
                href={`/s/${encodeURIComponent(seedId)}/audiotool`}
                className="h-11 rounded-2xl border border-white/15 text-sm font-semibold flex items-center justify-center"
              >
                Try Audiotool (free)
              </a>
            </div>
          </div>
          <button type="button" onClick={onBack} className="mt-3 text-sm text-muted-foreground">
            ← Back
          </button>
        </>
      )}
    </>
  );
}
